// ============================================================
// SideQuests - Bottom Navigation (Track C)
// Thumb-reachable tabs: Home, Quests, Community, Messages, Profile.
// Unread notification badge (Track D) - live Firestore listener,
// own-uid only, no client writes beyond marking read.
// Tap targets ≥ 44px; safe-area inset for iOS home indicator.
// ============================================================

import React, { useEffect, useState } from 'react';
import {
  AppNotification,
  subscribeNotifications,
  unreadCount,
} from '../services/notifications';
import { useAuth } from '../context/AuthContext';
/** Map a route page id to the active bottom-nav tab id.
 * `/quests` is the canonical browse route, so it highlights QUESTS.
 * `/feed` is kept here only for the legacy redirect window; it must still
 * resolve to the QUESTS tab so the highlight never flickers mid-redirect. */
export function activeNavId(page: string): string {
  if (page.startsWith('quest/')) return 'home';
  if (page === 'inbox') return 'messages';
  if (page === 'community') return 'community';
  if (page === 'quests') return 'quests';
  if (page === 'feed') return 'quests';
  if (page === 'profile' || page.startsWith('profile?')) return 'profile';
  if (page === 'notifications') return 'notifications';
  if (page === 'my-quests') return 'quests';
  return page;
}

interface BottomNavProps {
  page: string;
  onNavigate: (page: string) => void;
}

const navItems = [
  { id: 'home', label: 'Home', icon: 'home', page: 'home' },
  { id: 'quests', label: 'Quests', icon: 'compass', page: 'quests' },
  { id: 'community', label: 'Community', icon: 'people', page: 'community' },
  { id: 'messages', label: 'Messages', icon: 'chat', page: 'inbox' },
  { id: 'profile', label: 'Profile', icon: 'user', page: 'profile' },
];

function NavIcon({ icon }: { icon: string }) {
  switch (icon) {
    case 'home':
      return (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" />
        </svg>
      );
    case 'compass':
      return (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="10" /><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
        </svg>
      );
    case 'people':
      return (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      );
    case 'chat':
      return (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      );
    case 'user':
      return (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
        </svg>
      );
    default:
      return null;
  }
}

export default function BottomNav({ page, onNavigate }: BottomNavProps) {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const active = activeNavId(page);

  useEffect(() => {
    if (!user) { setNotifications([]); return; }
    const unsub = subscribeNotifications(user.uid, (items) => setNotifications(items), () => {});
    return unsub;
  }, [user]);

  const unread = unreadCount(notifications);

  return (
    <nav className="bottom-nav" aria-label="Primary">
      {navItems.map((item) => (
        <button
          key={item.id}
          className={`nav-item ${active === item.id ? 'active' : ''}`}
          onClick={() => onNavigate(item.page)}
          style={{ minHeight: 52, minWidth: 56 }}
        >
          <span style={{ position: 'relative', display: 'inline-flex' }}>
            <NavIcon icon={item.icon} />
            {item.id === 'messages' && unread > 0 && (
              <span
                className="nav-unread-badge"
                aria-label={`${unread} unread notifications`}
              >
                {unread > 9 ? '9+' : unread}
              </span>
            )}
          </span>
          <span className="nav-label">{item.label}</span>
        </button>
      ))}
    </nav>
  );
}
